import { FC, useCallback, useMemo, useState, useEffect } from "react";
import {
  createEditor,
  Descendant,
  Editor,
  Element as SlateElement,
  Transforms,
} from "slate";
import {
  Slate,
  Editable,
  withReact,
  RenderElementProps,
  RenderLeafProps,
} from "slate-react";
import { withHistory } from "slate-history";
import {
  Box,
  IconButton,
  Divider,
  Tooltip,
  useMediaQuery,
  Typography,
  FormLabel,
  TextField,
} from "@mui/material";
import { CustomElement } from "./RichTextEditor.types";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LinkIcon from "@mui/icons-material/Link";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";

import { Element } from "./Element";
import { Leaf } from "./Leaf";
import Popover from "@mui/material/Popover";
import { MarkButton } from "./MarkButton";
import { BlockButton } from "./BlockButton";

const defaultValue: Descendant[] = [
  {
    type: "paragraph",
    children: [{ text: "" }],
  },
];

interface IRichTextEditorProps {
  value?: Descendant[];
  readOnly?: boolean;
  placeholder?: string;
  fontSize?: string;
  onChange?: (value: Descendant[]) => void;
  isDisabled?: boolean;
  isError?: boolean;
  label?: string;
  required?: boolean;
  tooltipText?: string;
}

const RichTextEditor: FC<IRichTextEditorProps> = ({
  value = defaultValue,
  readOnly = false,
  placeholder,
  fontSize = "16px",
  onChange,
  isDisabled = false,
  isError = false,
  label,
  required = false,
  tooltipText,
}) => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const editor = useMemo(() => {
    const editorInstance = withHistory(withReact(createEditor()));


    const { isInline } = editorInstance;
    editorInstance.isInline = (element) =>
      element.type === "link" ? true : isInline(element);

    return editorInstance;
  }, []);
  const [linkUrl, setLinkUrl] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [linkLabel, setLinkLabel] = useState("");
  const [linkError, setLinkError] = useState(false);
  const [linkLabelError, setLinkLabelError] = useState(false);

  const isLinkActive = () => {
    const [match] = Array.from(
      Editor.nodes(editor, {
        match: (n) =>
          SlateElement.isElement(n) && (n as CustomElement).type === "link",
      })
    );
    return !!match;
  };

  const insertLink = (url: string, label: string) => {
    if (!url) return;
    const { selection } = editor;
    if (!selection) return;

    const link: CustomElement = {
      type: "link",
      url,
      children: [{ text: label }],
    };
    Transforms.insertNodes(editor, link, { at: selection.anchor });
  };

  const removeLink = () => {
    Transforms.unwrapNodes(editor, {
      match: (n) =>
        SlateElement.isElement(n) && (n as CustomElement).type === "link",
    });
  };

  const handleLinkSubmit = () => {
    const isUrlEmpty = !linkUrl.trim();
    const isLabelEmpty = !linkLabel.trim();

    setLinkError(isUrlEmpty);
    setLinkLabelError(isLabelEmpty);

    if (isUrlEmpty || isLabelEmpty) {
      return;
    }

    if (isLinkActive()) {
      removeLink();
    }

    insertLink(linkUrl.trim(), linkLabel.trim());
    resetLinkForm();
    setAnchorEl(null);
  };

  const resetLinkForm = () => {
    setLinkUrl("");
    setLinkLabel("");
    setLinkError(false);
    setLinkLabelError(false);
  };

  // Set initial content
  useEffect(() => {
    editor.children = value;
  }, [editor, value]);

  // Editor configurations
  const { normalizeNode } = editor;
  editor.normalizeNode = ([node, path]) => {
    if (path.length === 0) {
      if (
        Editor.isEmpty(editor, node as CustomElement) &&
        (!SlateElement.isElement(node) || !node.type)
      ) {
        const paragraph: CustomElement = {
          type: "paragraph",
          children: [{ text: "" }],
        };
        Transforms.insertNodes(editor, paragraph, { at: path.concat(0) });
        return;
      }
    }
    normalizeNode([node, path]);
  };

  const { insertBreak } = editor;
  editor.insertBreak = () => {
    const { selection } = editor;
    if (selection) {
      //@ts-ignore
      const [node] = Editor.nodes(editor, {
        match: (n) => SlateElement.isElement(n) && n.type === "list-item",
      });

      if (node) {
        const [, path] = node;
        const isEmpty = Editor.isEmpty(editor, node[0]);

        if (isEmpty) {
          Transforms.unwrapNodes(editor, {
            match: (n) =>
              SlateElement.isElement(n) &&
              ["numbered-list", "bulleted-list"].includes(n.type),
            split: true,
          });
          Transforms.setNodes(editor, { type: "paragraph" });
          return;
        }
      }
    }
    insertBreak();
  };

  const renderElement = useCallback(
    (props: RenderElementProps) => <Element {...props} />,
    []
  );
  const renderLeaf = useCallback(
    (props: RenderLeafProps) => <Leaf {...props} />,
    []
  );

  return (
    <Slate
      editor={editor}
      initialValue={value}
      onValueChange={(newValue) => {
        if (onChange) {
          onChange(newValue);
        }
      }}
    >
      <Box
        sx={{
          border: "1px solid",
          borderColor: isError ? "#ff1744" : "#ddd",
          borderRadius: 1,
          minHeight: isDisabled ? "unset" : "200px",
          height: isDisabled ? "80px" : "auto",
          padding: 1,
          fontSize: fontSize,
          width: "100%",
          maxWidth: isMobile ? "calc(100vw - 48px)" : "none",
          position: "relative",
          "& .editor-label": {
            position: "absolute",
            top: -9,
            left: 10,
            padding: "0 4px",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "0.875rem",
          },
        }}
      >
        {label && (
          <Box className="editor-label">
            <FormLabel
              sx={{
                color: isError ? "#ff1744" : "rgba(0, 0, 0, 0.6)",
                fontSize: "0.8rem",
              }}
            >
              {label}
            </FormLabel>
            {required && (
              <Typography
                component="span"
                color={isError ? "error" : "rgba(0, 0, 0, 0.6)"}
                sx={{ ml: 0.2, fontSize: "0.8rem" }}
              >
                *
              </Typography>
            )}
            {tooltipText && (
              <Tooltip title={tooltipText}>
                <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
                  <InfoOutlinedIcon sx={{ fontSize: "16px" }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
        {!readOnly && (
          <Box sx={{ mb: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            <MarkButton format="bold" />
            <MarkButton format="italic" />
            <MarkButton format="underline" />
            <MarkButton format="strikethrough" />
            <Divider orientation="vertical" flexItem />
            <BlockButton format="block-quote" />
            <BlockButton format="heading-one" />
            <BlockButton format="heading-two" />
            <Divider orientation="vertical" flexItem />
            <BlockButton format="numbered-list" />
            <BlockButton format="bulleted-list" />
            <Divider orientation="vertical" flexItem />
            <BlockButton format="left" />
            <BlockButton format="center" />
            <BlockButton format="right" />
            <BlockButton format="center" />
            <Tooltip title="Insert Link">
              <IconButton
                onMouseDown={(e) => {
                  e.preventDefault();
                  resetLinkForm();
                  setAnchorEl(e.currentTarget);
                }}
              >
                <LinkIcon
                  color={isLinkActive() ? "primary" : "inherit"}
                  fontSize="small"
                />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => {
            resetLinkForm();
            setAnchorEl(null);
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <Box
            sx={{
              padding: "0.75rem",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" fontSize="0.9rem">
              Insert Link
            </Typography>
            <TextField
              size="small"
              margin="dense"
              label="Link Label"
              type="text"
              variant="outlined"
              value={linkLabel}
              onChange={(e) => {
                setLinkLabel(e.target.value);
                setLinkLabelError(false);
              }}
              error={linkLabelError}
              helperText={linkLabelError ? "Label is required" : ""}
            />

            <TextField
              size="small"
              margin="dense"
              label="Link URL"
              type="text"
              variant="outlined"
              value={linkUrl}
              onChange={(e) => {
                setLinkUrl(e.target.value);
                setLinkError(false); // Clear error when user types
              }}
              error={linkError}
              helperText={linkError ? "Link is required" : ""}
            />

            <Box
              sx={{ display: "flex", justifyContent: "flex-end", marginTop: 1 }}
            >
              <IconButton onClick={() => setAnchorEl(null)} size="small">
                <CloseIcon />
              </IconButton>
              <IconButton onClick={handleLinkSubmit} size="small">
                <DoneIcon />
              </IconButton>
            </Box>
          </Box>
        </Popover>
        <Editable
          disabled={isDisabled}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          placeholder={placeholder}
          readOnly={isDisabled ? isDisabled : readOnly}
          spellCheck
          autoFocus
          style={{
            outline: "none",
            overflow: "auto",
            fontSize: fontSize,
          }}
        />
      </Box>
    </Slate>
  );
};

export default RichTextEditor;
